const PKCE_STORAGE_KEY = 'nice_oidc_pkce';

export interface PkceSession {
  codeVerifier: string;
  state: string;
  redirectUri: string;
}

export function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function generateCodeVerifier(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

export async function generateCodeChallenge(codeVerifier: string): Promise<string> {
  const data = new TextEncoder().encode(codeVerifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}

export function generateState(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

export function buildOidcState(returnOrigin: string): string {
  const nonce = generateState();
  const origin = returnOrigin.trim().replace(/\/+$/, '');
  return `${nonce}|${origin}`;
}

export function parseOidcReturnOrigin(state: string | null | undefined): string | null {
  if (!state) {
    return null;
  }

  const separatorIndex = state.indexOf('|');
  if (separatorIndex === -1) {
    return null;
  }

  const origin = state
    .slice(separatorIndex + 1)
    .trim()
    .replace(/\/+$/, '');
  return origin || null;
}

export function storePkceSession(session: PkceSession): void {
  sessionStorage.setItem(PKCE_STORAGE_KEY, JSON.stringify(session));
}

export function consumePkceSession(expectedState: string | null): PkceSession | null {
  const raw = sessionStorage.getItem(PKCE_STORAGE_KEY);
  sessionStorage.removeItem(PKCE_STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    const session = JSON.parse(raw) as PkceSession;
    if (expectedState && session.state !== expectedState) {
      return null;
    }
    return session;
  } catch {
    return null;
  }
}
